import React, { MouseEventHandler, ReactNode } from 'react';
import Avatar from './Avatar';
import BaseListItem, { RowMarking } from './BaseListItem';
import Icon from './Icon';
import { IconKey } from 'flow/components/ui-components/Icon';
import { TypedMap } from 'flow/types/TypedMap';
import { Contact } from 'flow/types/Contact';
import CallAdaptiveButton from '../CallAdaptiveButton';

interface Props {
    onClick: MouseEventHandler;
    marking?: RowMarking;
    type: keyof typeof PbxListItemSettings;
    title: string;
    description?: string;
    dataTestId?: string;
    notificationCount?: number;
    children?: ReactNode;
    phoneNumber?: string;
    additionalCallInfo?: TypedMap<Contact>;
}

const PbxListItem = ({
    onClick,
    marking,
    type,
    title,
    description,
    notificationCount,
    children,
    phoneNumber,
    additionalCallInfo,
    dataTestId,
}: Props) => {
    const transferContact =
        getIsIVRType(type) && additionalCallInfo ? additionalCallInfo : {};

    return (
        <BaseListItem
            marking={marking}
            onClick={onClick}
            dataTestId={dataTestId}
        >
            <div className="flex w-full grow flex-col gap-2.5">
                <div className="flex w-full items-center justify-center gap-x-2">
                    <Avatar
                        className={PbxListItemSettings[type].avatarBackground}
                        count={notificationCount}
                    >
                        <Icon
                            iconKey={PbxListItemSettings[type].iconKey}
                            className="text-iconOnBrand"
                        />
                    </Avatar>

                    <div className="flex-1 justify-center truncate">
                        <div
                            className="justify-between truncate text-base/5 text-gray-800"
                            data-test-id={'card-title-item-primary'}
                        >
                            {title}
                        </div>
                        <div
                            className="truncate text-sm text-gray-500"
                            data-test-id={'card-title-item-secondary'}
                        >
                            {description}
                        </div>
                    </div>

                    {children}

                    {!!phoneNumber && (
                        <div className="sr-only-until-hover-or-focus flex gap-x-2">
                            <CallAdaptiveButton
                                phoneNumber={phoneNumber}
                                transferContact={transferContact}
                                activeBg={true}
                            />
                        </div>
                    )}
                </div>
            </div>
        </BaseListItem>
    );
};

export default PbxListItem;

interface PbxItemSettings {
    iconKey: IconKey;
    avatarBackground: string;
}

const PbxListItemSettings: Record<string, PbxItemSettings> = {
    conference: {
        avatarBackground: 'bg-pbx-conference',
        iconKey: 'video',
    },
    conference_call: {
        avatarBackground: 'bg-pbx-conference',
        iconKey: 'phone',
    },
    connection: {
        avatarBackground: 'bg-pbx-connection',
        iconKey: 'settingsInputHdmi',
    },
    ivr: {
        avatarBackground: 'bg-pbx-refer',
        iconKey: 'ivr',
    },
    liveChat: {
        avatarBackground: 'bg-pbx-channel',
        iconKey: 'forum',
    },
    queue: {
        avatarBackground: 'bg-pbx-huntgroup',
        iconKey: 'queuelogin',
    },
    omniQueue: {
        avatarBackground: 'bg-pbx-huntgroup',
        iconKey: 'omni-queue',
    },
    voicemail: {
        avatarBackground: 'bg-pbx-voicemail',
        iconKey: 'voicemail',
    },
    overview: {
        avatarBackground: 'bg-main',
        iconKey: 'supervisor',
    },
    aicoworker: {
        avatarBackground: 'bg-main',
        iconKey: 'support_agent',
    },
};

const getIsIVRType = (type: string) => {
    return type === 'ivr';
};
